// Lazy-loaded component registry for code splitting
// This replaces direct imports with React.lazy() for better bundle splitting

import React, {Suspense} from 'react';
import {Navigate} from 'react-router-dom';
import CellLoader from '@/components/loading/CellLoader';
import i18n from 'i18n';

// Create a wrapper for lazy components with loading fallback
const withSuspense = (LazyComponent: React.LazyExoticComponent<React.ComponentType<any>>) => {
    return (props: any) => (
        <Suspense fallback={<CellLoader />}>
            <LazyComponent {...props} />
        </Suspense>
    );
};

// Lazy load major route components
const NoAccessView = React.lazy(() => import('../views/home/NoAccessView'));
const UserListView = React.lazy(() => import('../views/users/UserListView'));
const SearchView = React.lazy(() => import('../views/search/SearchView'));
const EventsListView = React.lazy(() => import('../views/events/EventsListView'));
const EventsDashboardView = React.lazy(() => import('../views/events/EventsDashboardView'));

// Admin views - grouped for better chunking
const ActionListView = React.lazy(() => import('../views/admin/actions/ActionListView'));
const RuleListView = React.lazy(() => import('../views/admin/rules/RuleListView'));
const EvaluationsListView = React.lazy(() => import('../views/admin/evaluation/EvaluationsListView'));
const LicenseView = React.lazy(() => import('../views/admin/license/LicenseView'));
const ApiTokenListView = React.lazy(() => import('../views/admin/api_tokens/ApiTokenListView'));
const ChannelListView = React.lazy(() => import('../views/admin/channel/ChannelListView'));

// Channel detail wrappers are loaded inline in the registry

// Email fetch views
const EmailFetchCredentialsListView = React.lazy(() => import('../views/admin/email_fetch/EmailFetchCredentialsListView'));
const EmailFetchAccountsListView = React.lazy(() => import('../views/admin/email_fetch/EmailFetchAccountsListView'));
const EmailFetchTasksListView = React.lazy(() => import('../views/admin/email_fetch/EmailFetchTasksListView'));

// Other admin views
const DictionaryListView = React.lazy(() => import('../views/admin/dictionaries/DictionaryListView'));
const ConfigurationListView = React.lazy(() => import('../views/admin/configuration/ConfigurationListView'));
const TranscriptionTasksListView = React.lazy(() => import('../views/admin/transcription_tasks/TranscriptionTasksListView'));
const TranscriptionTasksNotSentListView = React.lazy(() => import('../views/admin/transcription_tasks/TranscriptionTasksNotSentListView'));
const TranscriptionTasksInTranscriptionListView = React.lazy(() => import('../views/admin/transcription_tasks/TranscriptionTasksInTranscriptionListView'));
const AudioAnonymizationTasksListView = React.lazy(() => import('../views/admin/audio_anonymization_tasks/AudioAnonymizationTasksListView'));
const CeleryQueueView = React.lazy(() => import('../views/admin/celery_queue/CeleryQueueView'));

// Search index views
const SearchIndexFullListView = React.lazy(() => import('../views/admin/search_index/SearchIndexFullListView'));
const SearchIndexAnonListView = React.lazy(() => import('../views/admin/search_index/SearchIndexAnonListView'));

// Quality assurance views
const QualityAssuranceListView = React.lazy(() => import('../views/admin/quality_assurance/QualityAssuranceListView'));

// Roles & Permissions views
const RolesListView = React.lazy(() => import('../views/admin/roles_permissions/RolesListView'));
const PermissionsListView = React.lazy(() => import('../views/admin/roles_permissions/PermissionsListView'));
const RestrictionProfilesView = React.lazy(() => import('../views/admin/roles_permissions/RestrictionProfilesView'));
const AuditLogView = React.lazy(() => import('../views/admin/roles_permissions/AuditLogView'));

// Notification views
const NotificationsListView = React.lazy(() => import('../views/admin/notification/NotificationsListView'));
const MyNotificationsListView = React.lazy(() => import('../views/admin/notification/MyNotificationsListView'));
const OutboundNotificationLogsHubView = React.lazy(() => import('../views/admin/notification/OutboundNotificationLogsHubView'));
const OutboundNotificationLogsView = React.lazy(() => import('../views/admin/notification/OutboundNotificationLogsView'));

// Other views
const CustomerListView = React.lazy(() => import('../views/customers/CustomerListView'));
const CustomersDashboardView = React.lazy(() => import('../views/customers/CustomersDashboardView'));
const AgentListView = React.lazy(() => import('../views/agents/AgentListView'));
const AgentDashboardView = React.lazy(() => import('../views/agents/AgentDashboardView'));
const AgentEventsView = React.lazy(() => import('../views/agents/AgentEventsView'));
const AgentTeamView = React.lazy(() => import('../views/agents/AgentTeamView'));
const AssignmentsListView = React.lazy(() => import('../views/tasks/AssignmentsListView'));
const TeamListView = React.lazy(() => import('../views/teams/TeamListView'));
const TeamMembersListView = React.lazy(() => import('../views/teams/TeamMembersListView'));
const TeamEventsView = React.lazy(() => import('../views/teams/TeamEventsView'));

// Stories views
const StoryListView = React.lazy(() => import('../views/stories/StoryListView'));

// Settings views
const UserPermissionsView = React.lazy(() => import('../views/settings/UserPermissionsView'));
const PreferencesView = React.lazy(() => import('../views/settings/PreferencesView'));
const AdditionalAppsSettingsView = React.lazy(() => import('../views/settings/AdditionalAppsSettingsView'));
const SwitchRoleSettingsView = React.lazy(() => import('../views/settings/SwitchRoleSettingsView'));

// Event detail views
const EventDetailViewWithMenuContext = React.lazy(() => import('../views/event_detail/EventDetailViewWithMenuContext'));

// Storyboard views
const StoryboardViewContainer = React.lazy(() => import('../views/storyboard/StoryboardViewContainer'));

// Insights views
const PublicStoryboardsListView = React.lazy(() => import('../views/insights/PublicStoryboardsListView'));

// Team detail views
const TeamDetailView = React.lazy(() => import('../views/teams/TeamDetailView'));

// Channel evaluation tasks
const ChannelEvaluationTasksListView = React.lazy(() => import('../views/admin/channel/ChannelEvaluationsTasksListView'));


// Error views
const NotFoundRedirect = React.lazy(() => import('../components/errors/NotFoundRedirect'));

// Lazy component registry with Suspense wrappers
export const lazyComponentRegistry: Record<string, React.ComponentType<any>> = {
    // Home section - keep simple components non-lazy
    'home/WelcomeView': () => <div>{i18n.t('jt_welcome_to_fsvision.text')}</div>,
    'home/NoAccessView': withSuspense(NoAccessView),
  
    // Errors section - keep simple components non-lazy
    'errors/NotFoundView': withSuspense(NotFoundRedirect),
  
    // Discover section
    'users/UserListView': withSuspense(UserListView),
    'search/SearchView': withSuspense(SearchView),
  
    // Conversations section  
    'events/EventsListView': withSuspense(EventsListView),
    'events/EventsDashboardView': withSuspense(EventsDashboardView),
  
    // Admin section
    'admin/ActionListView': withSuspense(ActionListView),
    'admin/RuleListView': withSuspense(RuleListView),
    'admin/rules-actions/ActionsListView': withSuspense(ActionListView),
    'admin/rules-actions/RulesListView': withSuspense(RuleListView),
    'admin/evaluation/EvaluationsListView': withSuspense(EvaluationsListView),
    'admin/license/LicenseView': withSuspense(LicenseView),
    'admin/api_tokens/ApiTokenListView': withSuspense(ApiTokenListView),
    'admin/channel/ChannelListView': withSuspense(ChannelListView),
  
    // Channel detail views - lazy load the wrapper and extract components
    'admin/channel/ChannelEvaluationTasksView': withSuspense(
        React.lazy(() => 
            import('../views/admin/channel/ChannelDetailWrappers').then(module => ({
                default: module.ChannelEvaluationTasksWrapper
            }))
        )
    ),
    'admin/channel/ChannelRulesView': withSuspense(
        React.lazy(() => 
            import('../views/admin/channel/ChannelDetailWrappers').then(module => ({
                default: module.ChannelRulesWrapper
            }))
        )
    ),
    'admin/channel/ChannelActionsView': withSuspense(
        React.lazy(() => 
            import('../views/admin/channel/ChannelDetailWrappers').then(module => ({
                default: module.ChannelActionsWrapper
            }))
        )
    ),
  
    // Email fetch views
    'admin/email_fetch/EmailFetchCredentialsListView': withSuspense(EmailFetchCredentialsListView),
    'admin/email_fetch/EmailFetchAccountsListView': withSuspense(EmailFetchAccountsListView),
    'admin/email_fetch/EmailFetchTasksListView': withSuspense(EmailFetchTasksListView),
  
    // Other admin views
    'admin/dictionaries/DictionaryListView': withSuspense(DictionaryListView),
    'admin/configuration/ConfigurationListView': withSuspense(ConfigurationListView),
    'admin/transcription_tasks/TranscriptionTasksListView': withSuspense(TranscriptionTasksListView),
    'admin/transcription_tasks/TranscriptionTasksNotSentListView': withSuspense(TranscriptionTasksNotSentListView),
    'admin/transcription_tasks/TranscriptionTasksInTranscriptionListView': withSuspense(TranscriptionTasksInTranscriptionListView),
    'admin/audio_anonymization_tasks/AudioAnonymizationTasksListView': withSuspense(AudioAnonymizationTasksListView),
    'admin/celery_queue/CeleryQueueView': withSuspense(CeleryQueueView),
  
    // Admin Search Index views
    'admin/search_index/SearchIndexFullView': withSuspense(SearchIndexFullListView),
    'admin/search_index/SearchIndexAnonView': withSuspense(SearchIndexAnonListView),
  
    // Quality assurance views
    'admin/quality_assurance/QualityAssuranceListView': withSuspense(QualityAssuranceListView),
  
    // Roles & Permissions views
    'admin/roles_permissions/RolesListView': withSuspense(RolesListView),
    'admin/roles_permissions/PermissionsListView': withSuspense(PermissionsListView),
    'admin/roles_permissions/RestrictionProfilesView': withSuspense(RestrictionProfilesView),
    'admin/roles_permissions/AuditLogView': withSuspense(AuditLogView),
  
    // Notification views
    'admin/notification/NotificationsListView': withSuspense(NotificationsListView),
    'admin/notification/MyNotificationsListView': withSuspense(MyNotificationsListView),
    'admin/notification/OutboundNotificationLogsHubView': withSuspense(OutboundNotificationLogsHubView),
    'admin/notification/OutboundNotificationLogsView': withSuspense(OutboundNotificationLogsView),
  
    // Other sections
    'customers/CustomerListView': withSuspense(CustomerListView),
    'customers/CustomersDashboardView': withSuspense(CustomersDashboardView),
    'customers/CustomerDetailView': withSuspense(
        React.lazy(() => import('../views/customers/CustomerDetailView'))
    ),
    'customers/CustomerDetailDashboardView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/customers/CustomerDetailDashboardView');
            return { 
                default: (props: any) => (
                    <module.default 
                        {...props} 
                        customerId={props.params?.customerId} 
                    />
                )
            };
        })
    ),
    'customers/CustomerCallsListView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/customers/CustomerCallsListView');
            return { 
                default: (props: any) => (
                    <module.default 
                        {...props} 
                        customerId={props.params?.customerId} 
                    />
                )
            };
        })
    ),
    'agents/AgentListView': withSuspense(AgentListView),
    'agents/AgentDashboardView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/agents/AgentDashboardView');
            return { 
                default: (props: any) => {
                    const agentId = props.params?.agentId;
                    return <module.default {...props} agentId={agentId || '0'} />;
                }
            };
        })
    ),
    'agents/AgentEventsView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/agents/AgentEventsView');
            return { 
                default: (props: any) => {
                    const agentId = props.params?.agentId;
                    return <module.default {...props} agentId={agentId || '0'} />;
                }
            };
        })
    ),
    'agents/AgentTeamView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/agents/AgentTeamView');
            return { 
                default: (props: any) => {
                    const agentId = props.params?.agentId;
                    return <module.default {...props} agentId={agentId || '0'} />;
                }
            };
        })
    ),
    'tasks/AssignmentsListView': withSuspense(AssignmentsListView),
    'teams/TeamListView': withSuspense(TeamListView),
  
    // Stories views with different types
    'stories/MyStoriesListView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/stories/StoryListView');
            return { 
                default: (props: any) => <module.default {...props} storyListType="myStories" />
            };
        })
    ),
    'stories/SharedStoriesListView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/stories/StoryListView');
            return { 
                default: (props: any) => <module.default {...props} storyListType="sharedStories" />
            };
        })
    ),
    'stories/PublicStoriesListView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/stories/StoryListView');
            return { 
                default: (props: any) => <module.default {...props} storyListType="publicStories" />
            };
        })
    ),
  
    // Admin Quality Assurance with multi-parameter support
    'admin/quality_assurance/QualityAssuranceView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/admin/quality_assurance/QualityAssuranceListView');
            return { 
                default: (props: any) => (
                    <module.default 
                        {...props}
                        event_type={props.params?.event_type || 'call'}
                        date_type={props.params?.date_type || 'datetime'}
                    />
                )
            };
        })
    ),
  
    // Teams section with parameters
    'teams/TeamDetailView': withSuspense(TeamDetailView),
    'teams/TeamMembersView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/teams/TeamMembersListView');
            return { 
                default: (props: any) => {
                    const teamId = props.params?.teamId;
                    return <module.default teamId={teamId || '0'} />;
                }
            };
        })
    ),
    'teams/TeamMembersListView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/teams/TeamMembersListView');
            return { 
                default: (props: any) => {
                    const teamId = props.params?.teamId;
                    return <module.default {...props} teamId={teamId || '0'} />;
                }
            };
        })
    ),
    'teams/TeamEventsView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/teams/TeamEventsView');
            return { 
                default: (props: any) => {
                    const teamId = props.params?.teamId;
                    return <module.default {...props} teamId={teamId || '0'} />;
                }
            };
        })
    ),
  
    // Insights section
    'insights/AssignmentsListView': withSuspense(AssignmentsListView),
    'insights/PublicStoryboardsListView': withSuspense(PublicStoryboardsListView),
  
    // Stories section with different types
    'stories/MyStoriesView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/stories/StoryListView');
            const {StoryListType} = await import('../types/StoryListType');
            return { 
                default: (props: any) => <module.default {...props} storyListType={StoryListType.MyStories} />
            };
        })
    ),
    'stories/SharedStoriesView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/stories/StoryListView');
            const {StoryListType} = await import('../types/StoryListType');
            return { 
                default: (props: any) => <module.default {...props} storyListType={StoryListType.SharedStories} />
            };
        })
    ),
    'stories/TemplatesView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/stories/StoryListView');
            const {StoryListType} = await import('../types/StoryListType');
            return { 
                default: (props: any) => <module.default {...props} storyListType={StoryListType.StoryTemplates} />
            };
        })
    ),
  
    // Storyboard View with complex multi-parameter support
    'stories/StoryboardView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/storyboard/StoryboardViewContainer');
            return { 
                default: (props: any) => (
                    <module.default 
                        {...props}
                        activeStoryboardHashid={props.params?.activeStoryboardHashid || ''}
                        activeStoryUrl={props.params?.activeStoryUrl || ''}
                        activeStoryboardUrl={props.params?.activeStoryboardUrl || ''}
                        activeStoryObject={props.params?.activeStoryObject || ''}
                    />
                )
            };
        })
    ),
  
    // Events section with different types
    'events/CallEventsView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/events/EventsListView');
            return {default: (props: any) => <module.default {...props} activeType="call" />};
        })
    ),
    'events/CallsDashboardView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/events/EventsDashboardView');
            return {default: (props: any) => <module.default {...props} storyAssignedUrl="calls" />};
        })
    ),
    'events/ChatEventsView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/events/EventsListView');
            return {default: (props: any) => <module.default {...props} activeType="chat" />};
        })
    ),
    'events/ChatsDashboardView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/events/EventsDashboardView');
            return {default: (props: any) => <module.default {...props} storyAssignedUrl="chats" />};
        })
    ),
    'events/EmailEventsView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/events/EventsListView');
            return {default: (props: any) => <module.default {...props} activeType="email" />};
        })
    ),
    'events/EmailsDashboardView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/events/EventsDashboardView');
            return {default: (props: any) => <module.default {...props} storyAssignedUrl="emails" />};
        })
    ),
    'events/FeedbackEventsView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/events/EventsListView');
            return {default: (props: any) => <module.default {...props} activeType="feedback" />};
        })
    ),
    'events/FeedbackDashboardView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/events/EventsDashboardView');
            return {default: (props: any) => <module.default {...props} storyAssignedUrl="feedback" />};
        })
    ),
  
    // Parametrized events
    'events/CustomerEventsView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/events/EventsListView');
            return { 
                default: (props: any) => (
                    <module.default 
                        {...props} 
                        activeObjectType="customer" 
                        activeObject={props.params?.customerId || '0'} 
                    />
                )
            };
        })
    ),
    'events/AgentEventsView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/events/EventsListView');
            return { 
                default: (props: any) => (
                    <module.default 
                        {...props} 
                        activeObjectType="agent" 
                        activeObject={props.params?.agentId || '0'} 
                    />
                )
            };
        })
    ),
    'events/TeamEventsView': withSuspense(TeamEventsView),
  
    // Event detail view
    'events/EventDetailView': withSuspense(
        React.lazy(async () => {
            const module = await import('../views/event_detail/EventDetailViewWithMenuContext');
            return { 
                default: (props: any) => (
                    <module.default {...props} activeEventId={parseInt(props.params?.eventId || '0', 10)} />
                )
            };
        })
    ),
  
    // Hidden/Tab routes (visible: false)
    'events/EventSettingsView': (props: any) => <div>Event Settings for Event ID: {props.params?.eventId}</div>,
    'admin/channel/ChannelAnalyticsView': (props: any) => <div>Channel Analytics for Channel ID: {props.params?.channelId}</div>,
  
    // Settings views
    'settings/PreferencesView': withSuspense(PreferencesView),
    'settings/UserPermissionsView': withSuspense(UserPermissionsView),
    'settings/AdditionalAppsSettingsView': withSuspense(AdditionalAppsSettingsView),
    'settings/SwitchRoleSettingsView': withSuspense(SwitchRoleSettingsView),
  
  
    // Redirects - keep simple
    'admin/rules-actions': () => <Navigate to="/admin/rules-actions/rules" replace />,
    'admin/search_index': () => <Navigate to="/admin/search_index/full" replace />,
    'admin/notification': () => <Navigate to="/admin/notification/notifications" replace />,
    'stories': () => <Navigate to="/stories/my" replace />,
};

// Export the getComponent function for compatibility
export function getComponent(viewComponentPath: string): React.ComponentType<any> | null {
    const component = lazyComponentRegistry[viewComponentPath];
    if (!component) {
        console.warn(`Component not found in lazy registry: ${viewComponentPath}`);
        return null;
    }
    return component;
}
